import { Box, Chip, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import moment from "moment";
import { toast } from "react-toastify";
import ApiEndpoint from "../../config/ApiEndpoint";
import ApiServices from "../../config/ApiServices";
import {
  PageHeader,
  Card,
  DataTable,
  ActionButton,
} from "../shared";

const EVENT_LABELS = {
  manual_enabled: "Enabled",
  manual_disabled: "Disabled",
  subscription_expired: "Expired",
};

const Payments = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const token = props?.props?.profile?.token;
  const userType =
    props?.props?.profile?.user_type || props?.props?.profile?.data?.user_type;

  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadInbox = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        "x-access-token": token,
      };
      const data = await ApiServices.GetApiCall(
        ApiEndpoint.ADMIN_PAYMENTS_INBOX,
        headers
      );
      if (data?.status) {
        setItems(data.data?.items || []);
        setUnreadCount(data.data?.unread_count || 0);
      } else if (data?.message) {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error("Failed to load payments inbox");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const markRead = async ({ ids, all = false }) => {
    if (!token) return;
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    props.setIsLoaded?.(true);
    try {
      const body = all ? { all: true } : { ids };
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.ADMIN_PAYMENTS_INBOX_READ,
        JSON.stringify(body),
        headers
      );
      if (data?.status) {
        toast.success(data.message || "Marked as read");
        loadInbox();
      } else {
        toast.error(data?.message || "Failed to update inbox");
      }
    } catch (error) {
      toast.error("Failed to update inbox");
      console.error(error);
    } finally {
      props.setIsLoaded?.(false);
    }
  };

  useEffect(() => {
    if (userType === "admin") {
      loadInbox();
    }
  }, [userType, loadInbox]);
  if (userType !== "admin") {
    return (
      <Box>
        <PageHeader
          title="Payments"
          subtitle="Access denied. This page is for platform admins."
        />
      </Box>
    );
  }

  const columns = [
    {
      field: "id",
      label: "ID",
      width: 70,
    },
    {
      field: "organisation_name",
      label: "Club",
      render: (value, row) => (
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
            {value || `Org #${row.id_organisation}`}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: isDark ? "#94a3b8" : "#64748b",
            }}
          >
            ID {row.id_organisation}
          </Typography>
        </Box>
      ),
    },
    {
      field: "event_type",
      label: "Event",
      render: (value) => {
        const label = EVENT_LABELS[value] || value;
        const color =
          value === "subscription_expired"
            ? "warning"
            : value === "manual_disabled"
            ? "error"
            : "success";
        return <Chip size="small" label={label} color={color} variant="outlined" />;
      },
    },
    {
      field: "message",
      label: "Message",
      render: (value) => (
        <Typography sx={{ fontSize: "0.85rem", maxWidth: 420 }}>
          {value}
        </Typography>
      ),
    },
    {
      field: "is_read",
      label: "Status",
      render: (value) =>
        value ? (
          <Chip size="small" label="Read" variant="outlined" />
        ) : (
          <Chip size="small" label="Unread" color="primary" />
        ),
    },
    {
      field: "createdAt",
      label: "When",
      render: (value, row) => {
        const ts = value || row.created_at;
        return ts ? moment(ts).format("DD MMM YYYY, HH:mm") : "—";
      },
    },
  ];

  const renderActions = (row) => {
    if (row.is_read) return null;
    return (
      <Box onClick={(e) => e.stopPropagation()}>
        <ActionButton
          variant="secondary"
          size="small"
          onClick={() => markRead({ ids: [row.id] })}
        >
          Mark read
        </ActionButton>
      </Box>
    );
  };

  return (
    <Box>
      <PageHeader
        title="Payments"
        subtitle={`Manual paid access events and expiry notices. ${unreadCount} unread.`}
      >
        <ActionButton variant="secondary" onClick={loadInbox}>
          Refresh
        </ActionButton>
        {unreadCount > 0 && (
          <ActionButton
            variant="primary"
            onClick={() => markRead({ all: true })}
          >
            Mark all read
          </ActionButton>
        )}
      </PageHeader>

      <Card>
        <DataTable
          columns={columns}
          data={items}
          loading={loading}
          actions={renderActions}
          emptyMessage="No payment events yet"
          pagination={false}
          mobileTitleColumn="organisation_name"
          mobileHiddenColumns={["id"]}
        />
      </Card>
    </Box>
  );
};

export default Payments;
